import { Button } from 'src/components/Button';
import { SelectBox } from 'src/components/SelectBox';
import { Input } from 'src/components/Input';
import { Plus, Trash } from '@phosphor-icons/react';
import { Option } from 'src/components/SelectBox';
import { useEffect, useState } from 'react';
import { useContacts } from './contexts/ContactsContext';
import { useFieldMapping } from './contexts/FieldMappingContext';

export enum FilterOperator {
  CONTAINS = 'contains',
  EQUALS = 'equals',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  NOT_EMPTY = 'not_empty',
  EMPTY = 'empty',
}

interface ContactFilterType {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string;
}

interface ContactFilterProps {
  disabled?: boolean;
}

// Available filter operators
const FILTER_OPERATORS: Option[] = [
  { value: FilterOperator.CONTAINS, label: 'Contains' },
  { value: FilterOperator.EQUALS, label: 'Equals' },
  { value: FilterOperator.STARTS_WITH, label: 'Starts With' },
  { value: FilterOperator.ENDS_WITH, label: 'Ends With' },
  { value: FilterOperator.NOT_EMPTY, label: 'Is Not Empty' },
  { value: FilterOperator.EMPTY, label: 'Is Empty' },
];

export const ContactFilter = ({ disabled = false }: ContactFilterProps) => {
  const { filters, setFilters, getFilterSummary } = useContacts();
  const { availableCrmFields } = useFieldMapping();
  const [availableFields, setAvailableFields] = useState<Option[]>([]);

  useEffect(() => {
    const fields: Option[] = availableCrmFields.map((field) => ({
      value: field.value,
      label: field.label,
    }));

    setAvailableFields(fields);
  }, [availableCrmFields]);

  const addFilter = () => {
    const newFilter: ContactFilterType = {
      id: Math.random().toString(36).substr(2, 9),
      field: availableFields[0]?.value || '',
      operator: FilterOperator.CONTAINS,
      value: '',
    };
    setFilters([...filters, newFilter]);
  };

  const updateFilter = (id: string, updates: Partial<ContactFilterType>) => {
    setFilters(
      filters.map((filter) =>
        filter.id === id ? { ...filter, ...updates } : filter,
      ),
    );
  };

  const removeFilter = (id: string) => {
    setFilters(filters.filter((filter) => filter.id !== id));
  };

  return (
    <div className="bg-neutral-0 border border-neutral-200 rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Filter Contacts</h3>
        <span className="text-sm text-neutral-600">{getFilterSummary()}</span>
      </div>

      <div className="space-y-4">
        {filters.map((filter) => (
          <div key={filter.id} className="flex gap-4 items-end">
            <div className="flex-1">
              <SelectBox
                options={availableFields}
                value={
                  availableFields.find((f) => f.value === filter.field) || null
                }
                onChange={(value) => updateFilter(filter.id, { field: value })}
                variant="outlined"
                size="medium"
              />
            </div>
            <div className="flex-1">
              <SelectBox
                options={FILTER_OPERATORS}
                value={
                  FILTER_OPERATORS.find((o) => o.value === filter.operator) ||
                  null
                }
                onChange={(value) =>
                  updateFilter(filter.id, {
                    operator: value as FilterOperator,
                  })
                }
                variant="outlined"
                size="medium"
              />
            </div>
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter value..."
                value={filter.value}
                onChange={(e) =>
                  updateFilter(filter.id, { value: e.target.value })
                }
                disabled={
                  filter.operator === FilterOperator.NOT_EMPTY ||
                  filter.operator === FilterOperator.EMPTY
                }
              />
            </div>
            <Button
              type="button"
              variant="outlined"
              onClick={() => removeFilter(filter.id)}
              className="px-3 py-2"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Button
          type="button"
          variant="outlined"
          onClick={addFilter}
          className="flex items-center gap-2"
          disabled={disabled || availableFields.length === 0}
        >
          <Plus className="h-4 w-4" />
          Add Filter
        </Button>
      </div>
    </div>
  );
};

export type { ContactFilterType };
